import { Component, inject, input, output } from '@angular/core';
import { EmailVerificationService } from './email-verification.service';

/**
 * The three screens of the email-verification step: waiting for the link, verified somewhere
 * else, and a link that no longer works (DEV-11124 / ELIO-6959).
 *
 * All the state comes from `EmailVerificationService`, which the signup component provides, so
 * this renders the same everywhere. Only the two actions that need the brand's own stepper are
 * emitted: resending needs the state the brand stores, and changing the address has to walk the
 * customer back to step 1.
 *
 * Render it where the stepper would go, and hide the stepper while `isActive()` is true.
 */
@Component({
  selector: 'app-email-verification',
  imports: [],
  template: `
    @if (verification.verifiedInOtherTab()) {
    <section class="text-center py-5 animated fadeInUp">
      <i class="fas fa-check-circle fa-3x text-success mb-3"></i>
      <h2 class="h5-bold text-secondary-800 head-font">Email verified</h2>
      <p class="h6-regular text-secondary mt-2 mb-0">
        You verified your email in another tab.<br />
        Continue creating your account there — you can safely close this one.
      </p>
    </section>
    } @else if (verification.pending()) {
    <section class="text-center py-5 animated fadeInUp">
      <i class="fas fa-envelope-open-text fa-3x mb-3 text-secondary-800"></i>
      <h2 class="h5-bold text-secondary-800 head-font">Verify your email</h2>
      <p class="h6-regular text-secondary mt-2">
        We've sent a verification link to <strong>{{ email() }}</strong>.<br />
        Open it to continue creating your account. Don't forget to check your spam folder.
      </p>
      <div class="d-flex gap-3 justify-content-center align-items-center flex-wrap mt-4">
        <button type="button" class="btn btn-secondary head-font" (click)="resend.emit()"
          [disabled]="verification.resendCooldown() > 0 || verification.isSending()">
          @if (verification.resendCooldown() > 0) {
          Resend email ({{ verification.resendCooldown() }}s)
          } @else {
          Resend email
          }
        </button>
        <section class="d-flex justify-content-center align-items-center gap-1 link-color h7-bold cursor-pointer"
          (click)="changeEmail.emit()">
          <i class="fas fa-pen"></i>
          <a href="javascript:void(0)" class="link-color">
            Change email
          </a>
        </section>
      </div>
    </section>
    } @else if (verification.error()) {
    <section class="text-center py-5 animated fadeInUp">
      <i class="fas fa-exclamation-circle fa-3x text-danger mb-3"></i>
      <h2 class="h5-bold text-secondary-800 head-font">
        @if (verification.error() === 'expired') {
        This verification link has expired
        } @else {
        This verification link is not valid
        }
      </h2>
      <p class="h6-regular text-secondary mt-2">
        Please start again to receive a new verification email.
      </p>
      <button type="button" class="btn btn-secondary head-font mt-3" (click)="verification.restart()">
        Start again
      </button>
    </section>
    }
  `
})
export class EmailVerificationComponent {

  verification = inject(EmailVerificationService);

  /** The address the link was sent to, shown on the waiting screen. */
  email = input<string>('');

  /** Needs the brand's own state, so the signup component sends it. */
  resend = output<void>();

  /** Walks the customer back to step 1 to fix a typo. */
  changeEmail = output<void>();
}
